import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
	DialogChatProps,
	ChatMessage,
	Conversation,
} from "../../core/types/tsx/components/chat/DialogChat.types";
import { getImageUri } from "../../core/utils/imageLoad";
import { useAuthSessionContext } from "../routing/auth-session-store";
import {
	ChevronLeft,
	Clock3,
	Expand,
	ImageIcon,
	Info,
	LoaderCircle,
	MessageCircle,
	MessageSquare,
	Image,
	Search,
	Send,
	Check,
	CheckCheck,
	CheckCircle2,
	AlertTriangle,
	MapPin,
	ShieldCheck,
	Wallet,
	Wrench,
	XCircle,
	X,
} from "lucide-react";
import {
	useJoinOrderRoom,
	useLeaveOrderRoom,
	useMarkAsRead,
	useMessagesByUser,
	useOnChatFinalized,
	useOnMessageRead,
	useOnMessageReceived,
	useOnOrderMessage,
	useOnUserOffline,
	useOnUserOnline,
	useRegisterUser,
	useSendMessage,
	useUploadMessageFile,
} from "../../hooks/control/useMessage";
import {
	useOrder,
	useOrdersByClient,
	useProviderOrders,
} from "../../hooks/services/useOrders";
import { useCurrentUser } from "../../hooks/control/useUsers";
import {
	getOrderStatusChangeCopy,
	getOrderStatusVisual,
	normalizeOrderStatusLabel,
	type OrderStatusIconKey,
} from "../../core/utils/orderStatus";
import {
	SUPPORT_WHATSAPP_REPORT_NUMBER,
	buildSupportWhatsAppReportUrl,
} from "../../core/config/support.ts";
import Loader from "../../pages/loader";

type ServerUserSummary = {
	id?: number;
	names?: string | null;
	last_names?: string | null;
	image_profile?: string | null;
	gender?: string | null;
	gender_client?: string | null;
	date_of_birth?: string | null;
	age?: number | string | null;
};

type ServerMessage = {
	id?: number;
	reminent_id: number;
	recipient_id: number;
	order_id: number;
	quotation_id?: number;
	content_message: string;
	archive_url?: string | null;
	is_read?: boolean;
	sent_at?: string | Date;
	quotations?: {
		id?: number;
		proposed_price?: number;
		description_quotation?: string;
	};
	users_Recipient?: ServerUserSummary;
	users_Reminent?: ServerUserSummary;
};

type ServerOrder = {
	id?: number;
	code_order?: string;
	status_order?: string;
	clients?: {
		id?: number;
		user_id?: number;
		users?: ServerUserSummary & { id?: number };
	};
	providers?: {
		id?: number;
		company_name?: string;
		users?: ServerUserSummary & { id?: number };
	};
};

type ConversationVm = {
	id: string;
	orderId: number;
	orderCode: string;
	counterpartId: number;
	recipientUserId: number | null;
	name: string;
	preview: string;
	previewHasImage: boolean;
	avatarUrl?: string;
	unread: number;
	timeLabel: string;
};

type PaymentRegisteredMessagePayload = {
	type: "payment_registered_message";
	preview?: string;
	message?: string;
};

type OrderStatusChangedMessagePayload = {
	type: "order_status_changed_message";
	preview?: string;
	message?: string;
	previous_status?: string;
	new_status?: string;
	changed_at?: string;
};

const CLOSED_ORDER_STATUSES = new Set([
	"Completado",
	"Cancelado",
	"Pagado",
	"Garantia",
	"Soporte",
]);
const REPORT_BLOCKED_ORDER_STATUSES = new Set(["Completado", "Cancelado", "Pagado"]);
const CHAT_INFO_MODAL_STORAGE_PREFIX = "servix:web:chat-info-seen:small";

function toRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object") return null;
	return value as Record<string, unknown>;
}

function toText(value: unknown): string | null {
	if (typeof value !== "string") return null;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
}

function toPositiveNumber(value: unknown): number | null {
	const parsed = Number(value ?? 0);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function resolveGenderLabel(value: unknown): string {
	const raw = toText(value);
	if (!raw) return "No disponible";
	const normalized = raw
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "");

	if (normalized === "m" || normalized.includes("mascul")) return "Masculino";
	if (normalized === "f" || normalized.includes("femen")) return "Femenino";
	if (normalized.includes("no bin")) return "No binario";
	if (normalized.includes("otro")) return "Otro";

	return raw;
}

function calculateAgeFromDate(value: unknown): string {
	const raw = toText(value);
	if (!raw) return "No disponible";
	const birthDate = new Date(raw);
	if (Number.isNaN(birthDate.getTime())) return "No disponible";

	const today = new Date();
	let age = today.getFullYear() - birthDate.getFullYear();
	const monthDiff = today.getMonth() - birthDate.getMonth();
	if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
		age -= 1;
	}

	if (!Number.isFinite(age) || age < 0) return "No disponible";
	return String(age);
}

function getPayloadRecord(value: unknown): Record<string, unknown> | null {
	const root = toRecord(value);
	if (!root) return null;
	const nested = toRecord(root.data);
	return nested ?? root;
}

function normalizePayloadArray(raw: unknown): unknown[] {
	if (Array.isArray(raw)) return raw;
	if (!raw || typeof raw !== "object") return [];
	const candidate = raw as Record<string, unknown>;
	if (Array.isArray(candidate.items)) return candidate.items;
	if (Array.isArray(candidate.messages)) return candidate.messages;
	if (Array.isArray(candidate.data)) return candidate.data;
	return [];
}

function normalizeOrders(raw: unknown): ServerOrder[] {
	return normalizePayloadArray(raw)
		.map((item) => {
			if (!item || typeof item !== "object") return null;
			const order = item as Partial<ServerOrder>;
			if (!order.id) return null;
			return {
				id: Number(order.id),
				code_order:
					typeof (order as Record<string, unknown>).code_order === "string"
						? ((order as Record<string, unknown>).code_order as string)
						: undefined,
				status_order:
					typeof order.status_order === "string"
						? order.status_order
						: undefined,
				clients: order.clients,
				providers: order.providers,
			} as ServerOrder;
		})
		.filter((item): item is ServerOrder => Boolean(item));
}

function normalizeMessages(raw: unknown): ServerMessage[] {
	return normalizePayloadArray(raw)
		.map((item) => {
			if (!item || typeof item !== "object") return null;
			const msg = item as Partial<ServerMessage>;
			if (!msg.order_id || !msg.reminent_id || !msg.recipient_id) return null;
			return {
				id: msg.id,
				reminent_id: Number(msg.reminent_id),
				recipient_id: Number(msg.recipient_id),
				order_id: Number(msg.order_id),
				quotation_id:
					typeof msg.quotation_id === "number"
						? Number(msg.quotation_id)
						: undefined,
				content_message: String(msg.content_message ?? ""),
				archive_url: msg.archive_url,
				is_read: Boolean(msg.is_read),
				sent_at: msg.sent_at,
				quotations:
					msg.quotations && typeof msg.quotations === "object"
						? msg.quotations
						: undefined,
				users_Recipient: msg.users_Recipient,
				users_Reminent: msg.users_Reminent,
			} as ServerMessage;
		})
		.filter((item): item is ServerMessage => Boolean(item));
}

function normalizeOrder(raw: unknown): { status_order?: string } | null {
	if (!raw || typeof raw !== "object") return null;
	const candidate = raw as Record<string, unknown>;
	if (typeof candidate.status_order === "string") {
		return { status_order: candidate.status_order };
	}
	const nested = candidate.data as Record<string, unknown> | undefined;
	if (nested && typeof nested.status_order === "string") {
		return { status_order: nested.status_order as string };
	}
	return null;
}

function toFullName(user?: ServerUserSummary) {
	if (!user) return "";
	return [user.names, user.last_names].filter(Boolean).join(" ").trim();
}

function getInitials(name?: string) {
	if (!name) return "";
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return "";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function formatShortTime(value?: string | Date) {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";
	return parsed.toLocaleTimeString("es-VE", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
}

function formatConversationSidebarTimeLabel(value?: string | Date) {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";

	const today = new Date();
	const todayUtcDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const messageUtcDay = Date.UTC(
		parsed.getFullYear(),
		parsed.getMonth(),
		parsed.getDate(),
	);
	const diffDays = Math.floor((todayUtcDay - messageUtcDay) / (1000 * 60 * 60 * 24));

	if (diffDays <= 0) return formatShortTime(parsed);
	if (diffDays === 1) return "Ayer";

	return parsed.toLocaleDateString("es-ES", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

function getMessageDateKey(value?: string | Date) {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";
	const year = parsed.getFullYear();
	const month = String(parsed.getMonth() + 1).padStart(2, "0");
	const day = String(parsed.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function formatWhatsappDateLabel(value?: string | Date) {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";

	const today = new Date();
	const todayUtcDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const messageUtcDay = Date.UTC(
		parsed.getFullYear(),
		parsed.getMonth(),
		parsed.getDate(),
	);
	const diffDays = Math.floor((todayUtcDay - messageUtcDay) / (1000 * 60 * 60 * 24));

	if (diffDays === 0) return "Hoy";
	if (diffDays === 1) return "Ayer";

	return parsed.toLocaleDateString("es-ES", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

function resolveAttachmentUrl(raw?: string | null) {
	return getImageUri(raw) ?? "";
}

function resolveProfileImageUrl(raw?: string | null) {
	return getImageUri(raw) ?? "";
}

function parseQuotationMessagePayload(
	raw: string | undefined,
	fallbackQuotation?: ServerMessage["quotations"],
): {
	id: number;
	proposed_price: number;
	description_quotation: string;
	preview?: string;
} | null {
	const normalizedFromFallback =
		fallbackQuotation && Number(fallbackQuotation.id ?? 0) > 0
			? {
					id: Number(fallbackQuotation.id ?? 0),
					proposed_price: Number(fallbackQuotation.proposed_price ?? 0),
					description_quotation: String(
						fallbackQuotation.description_quotation ?? "",
					),
				}
			: null;

	if (!raw) return normalizedFromFallback;

	try {
		const parsed = JSON.parse(raw) as Record<string, unknown>;
		if (parsed?.type !== "quotation_message") return normalizedFromFallback;

		const quote =
			typeof parsed.quotation === "object" && parsed.quotation
				? (parsed.quotation as Record<string, unknown>)
				: null;
		const parsedId = Number(quote?.id ?? parsed.quotation_id ?? 0);
		if (!Number.isFinite(parsedId) || parsedId <= 0) return normalizedFromFallback;

		const proposedPrice = Number(quote?.proposed_price ?? parsed.proposed_price ?? 0);
		const description = String(
			quote?.description_quotation ?? parsed.description_quotation ?? "",
		);

		return {
			id: parsedId,
			proposed_price: Number.isFinite(proposedPrice) ? proposedPrice : 0,
			description_quotation: description,
			preview: typeof parsed.preview === "string" ? parsed.preview : undefined,
		};
	} catch {
		return normalizedFromFallback;
	}
}

function formatCurrency(value: number) {
	return `$${Number(value || 0).toFixed(2)}`;
}

function parsePaymentRegisteredMessagePayload(
	raw: string | undefined,
): PaymentRegisteredMessagePayload | null {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as PaymentRegisteredMessagePayload;
		if (parsed?.type !== "payment_registered_message") return null;
		return parsed;
	} catch {
		return null;
	}
}

function parseOrderStatusChangedMessagePayload(
	raw: string | undefined,
): OrderStatusChangedMessagePayload | null {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as OrderStatusChangedMessagePayload;
		if (parsed?.type !== "order_status_changed_message") return null;
		return parsed;
	} catch {
		return null;
	}
}

function parseSocketOrderStatus(payload: {
	orderId?: number;
	message?: string;
	data?: { content_message?: string };
}): { orderId: number; status: string } | null {
	const orderId = Number(payload?.orderId ?? 0);
	if (!Number.isFinite(orderId) || orderId <= 0) return null;

	const contentFromData =
		typeof payload?.data?.content_message === "string"
			? payload.data.content_message
			: undefined;
	const contentFromMessage =
		typeof payload?.message === "string" ? payload.message : undefined;

	const structured =
		parseOrderStatusChangedMessagePayload(contentFromData) ??
		parseOrderStatusChangedMessagePayload(contentFromMessage);
	const nextStatus = normalizeOrderStatusLabel(structured?.new_status);
	if (!nextStatus) return null;

	return { orderId, status: nextStatus };
}

function getMessagePreviewText(message: ServerMessage) {
	const quotePayload = parseQuotationMessagePayload(
		message.content_message,
		message.quotations,
	);
	if (quotePayload) {
		return (
			quotePayload.preview ||
			`Cotización #${quotePayload.id} por ${formatCurrency(quotePayload.proposed_price)}`
		);
	}
	const paymentNoticePayload = parsePaymentRegisteredMessagePayload(
		message.content_message,
	);
	if (paymentNoticePayload) {
		return paymentNoticePayload.preview || "Pago registrado";
	}
	const statusChangedPayload = parseOrderStatusChangedMessagePayload(
		message.content_message,
	);
	if (statusChangedPayload) {
		const statusCopy = getOrderStatusChangeCopy({
			newStatus: statusChangedPayload.new_status,
			previousStatus: statusChangedPayload.previous_status,
			fallbackMessage: statusChangedPayload.message,
		});
		return statusChangedPayload.preview || statusCopy.preview;
	}
	return String(message.content_message ?? "");
}

function getCounterpartFromOrder(
	order: ServerOrder,
	isProviderView: boolean,
): { id: number; recipientUserId: number | null; name: string } | null {
	const toPositive = (value: unknown): number | null => {
		const parsed = Number(value ?? 0);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	};

	if (isProviderView) {
		const usersRecord = order.clients?.users as Record<string, unknown> | undefined;
		const clientRecord = order.clients as Record<string, unknown> | undefined;
		const recipientUserId =
			toPositive(usersRecord?.id) ??
			toPositive(clientRecord?.user_id) ??
			toPositive(clientRecord?.userId);
		const id = recipientUserId ?? -Number(order.id ?? 0);
		const name = toFullName(order.clients?.users) || "Cliente";
		return { id, recipientUserId, name };
	}
	const usersRecord = order.providers?.users as Record<string, unknown> | undefined;
	const providerRecord = order.providers as Record<string, unknown> | undefined;
	const recipientUserId =
		toPositive(usersRecord?.id) ??
		toPositive(providerRecord?.user_id) ??
		toPositive(providerRecord?.userId);
	const id = recipientUserId ?? -Number(order.id ?? 0);
	const name =
		order.providers?.company_name ||
		toFullName(order.providers?.users) ||
		"Proveedor";
	return { id, recipientUserId, name };
}

function getCounterpartUserIdFromOrder(
	raw: unknown,
	isProviderView: boolean,
): number | null {
	if (!raw || typeof raw !== "object") return null;
	const order = raw as Record<string, unknown>;
	const data = (order.data as Record<string, unknown> | undefined) ?? order;
	const source = isProviderView
		? (data.clients as Record<string, unknown> | undefined)
		: (data.providers as Record<string, unknown> | undefined);
	if (!source) return null;
	const users = source.users as Record<string, unknown> | undefined;
	const candidate = users?.id ?? source.user_id ?? source.userId;
	const parsed = Number(candidate ?? 0);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function QuoteMessageCard({
	quoteCode,
	priceLabel,
	description,
	onViewDetails,
}: {
	quoteCode: string;
	priceLabel: string;
	description?: string;
	onViewDetails?: () => void;
}) {
	return (
		<div className='w-full min-w-0 rounded-2xl border border-(--border) bg-white px-3 py-2.5'>
			<div className='flex items-center justify-between gap-2'>
				<div className='min-w-0 text-sm font-semibold text-primary'>
					Actualización de Cotización
				</div>
				<div className='shrink-0 whitespace-nowrap rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-sky-700'>
					{quoteCode}
				</div>
			</div>

			{description ? (
				<div className='mt-1 line-clamp-1 text-sm text-neutral-500'>
					{description}
				</div>
			) : null}

			<div className='mt-2 flex items-end justify-between gap-2'>
				<div className='text-xs text-neutral-500'>Total pendiente</div>
				<div className='text-2xl font-extrabold text-primary'>{priceLabel}</div>
			</div>

			<div className='mt-2'>
				<button
					type='button'
					onClick={onViewDetails}
					className='w-full cursor-pointer rounded-full bg-primary px-3 py-1.5 text-center text-sm font-semibold text-white hover:bg-sky-600'
				>
					Ver más detalles
				</button>
			</div>
		</div>
	);
}

export function DialogChat({
	open,
	onClose,
	isProviderView = true,
	className,
	onUnreadCountChange,
	onIncomingMessage,
}: DialogChatProps) {
	const navigate = useNavigate();
	const { session } = useAuthSessionContext();
	const numericUserId = Number(session?.user?.id ?? 0);
	const { data: selfUserRaw } = useCurrentUser(open ? numericUserId : 0);

	const [screen, setScreen] = useState<"list" | "thread">("list");
	const [query, setQuery] = useState("");
	const [activeConversationId, setActiveConversationId] = useState("");
	const [onlineUserIds, setOnlineUserIds] = useState<Set<number>>(new Set());
	const [socketReady, setSocketReady] = useState(false);
	const [isSocketInitializing, setIsSocketInitializing] = useState(false);
	const [socketOrderStatusByOrder, setSocketOrderStatusByOrder] = useState<
		Record<number, string>
	>({});
	const [resolvedRecipientByOrder, setResolvedRecipientByOrder] = useState<
		Record<number, number>
	>({});
	const markedMessageIdsRef = useRef<Set<number>>(new Set());

	const selfPayload = useMemo(() => getPayloadRecord(selfUserRaw), [selfUserRaw]);
	const selfProviderId = Number(
		(selfPayload?.providers as Record<string, unknown> | undefined)?.id ?? 0,
	);
	const selfClientId = Number(
		(selfPayload?.clients as Record<string, unknown> | undefined)?.id ?? 0,
	);
	const ownerEntityId = isProviderView ? selfProviderId : selfClientId;

	const {
		data: allMessagesRaw,
		loading: loadingMessages,
		execute: refetchMessages,
	} = useMessagesByUser(numericUserId, {
		auto: open && numericUserId > 0,
	});
	const messageRefreshTimerRef = useRef<number | null>(null);
	const messageRefreshInFlightRef = useRef(false);
	const messageRefreshPendingRef = useRef(false);
	const scheduleMessagesRefreshRef = useRef<() => void>(() => undefined);
	const scheduleMessagesRefresh = useCallback(() => {
		if (messageRefreshTimerRef.current !== null) {
			window.clearTimeout(messageRefreshTimerRef.current);
		}

		messageRefreshTimerRef.current = window.setTimeout(() => {
			messageRefreshTimerRef.current = null;
			if (messageRefreshInFlightRef.current) {
				messageRefreshPendingRef.current = true;
				return;
			}

			messageRefreshInFlightRef.current = true;
			void refetchMessages()
				.finally(() => {
					messageRefreshInFlightRef.current = false;
					if (!messageRefreshPendingRef.current) return;
					messageRefreshPendingRef.current = false;
					window.setTimeout(() => {
						scheduleMessagesRefreshRef.current();
					}, 0);
				});
		}, 150);
	}, [refetchMessages]);
		useEffect(() => {
			scheduleMessagesRefreshRef.current = scheduleMessagesRefresh;
		}, [scheduleMessagesRefresh]);
	const {
		data: rawClientOrders,
		loading: loadingOrdersClient,
		execute: refetchClientOrders,
	} = useOrdersByClient(isProviderView ? 0 : ownerEntityId, 1, 200);
	const {
		data: rawProviderOrders,
		loading: loadingOrdersProvider,
		refreshProviderOrders: refetchProviderOrders,
	} = useProviderOrders(isProviderView ? selfProviderId || null : null, 1, 200);
	const { register } = useRegisterUser();
	const { join } = useJoinOrderRoom();
	const { leave } = useLeaveOrderRoom();
	const { send: sendMessage, loading: sendingMessage } = useSendMessage();
	const { markAsRead } = useMarkAsRead();
	const registerRef = useRef(register);
	const joinRef = useRef(join);
	const leaveRef = useRef(leave);
	const markAsReadRef = useRef(markAsRead);
	useEffect(() => {
		registerRef.current = register;
	}, [register]);

	useEffect(() => {
		joinRef.current = join;
	}, [join]);

	useEffect(() => {
		leaveRef.current = leave;
	}, [leave]);

	useEffect(() => {
		markAsReadRef.current = markAsRead;
	}, [markAsRead]);

	const isLoadingOrders = isProviderView ? loadingOrdersProvider : loadingOrdersClient;
	const allOrders = useMemo(
		() => normalizeOrders(isProviderView ? rawProviderOrders : rawClientOrders),
		[isProviderView, rawProviderOrders, rawClientOrders],
	);

	const orderCodeByOrderId = useMemo(() => {
		const next = new Map<number, string>();
		allOrders.forEach((order) => {
			const orderId = Number(order.id ?? 0);
			if (!orderId) return;
			const code = String(order.code_order ?? "").trim();
			next.set(orderId, code || String(orderId));
		});
		return next;
	}, [allOrders]);

	const allUserMessages = useMemo(() => {
		if (!numericUserId) return [];
		return normalizeMessages(allMessagesRaw).filter(
			(m) => m.reminent_id === numericUserId || m.recipient_id === numericUserId,
		);
	}, [allMessagesRaw, numericUserId]);

	const conversationsVm = useMemo<ConversationVm[]>(() => {
		const groups = new Map<
			string,
			{
				list: ServerMessage[];
				counterpartId: number;
				recipientUserId: number | null;
				orderId: number;
				name?: string;
				avatarUrl?: string;
			}
		>();

		allUserMessages.forEach((msg) => {
			const counterpartId =
				msg.reminent_id === numericUserId ? msg.recipient_id : msg.reminent_id;
			const key = `${msg.order_id}`;
			const group = groups.get(key);
			if (group) {
				group.list.push(msg);
				if (!group.recipientUserId || group.recipientUserId <= 0) {
					group.recipientUserId = counterpartId;
				}
				if (!group.counterpartId || group.counterpartId <= 0) {
					group.counterpartId = counterpartId;
				}
				return;
			}
			groups.set(key, {
				list: [msg],
				counterpartId,
				recipientUserId: counterpartId,
				orderId: msg.order_id,
				avatarUrl: resolveProfileImageUrl(
					msg.reminent_id === counterpartId
						? msg.users_Reminent?.image_profile
						: msg.users_Recipient?.image_profile,
				),
			});
		});

		allOrders.forEach((order) => {
			const orderId = Number(order.id ?? 0);
			if (!orderId) return;
			const counterpart = getCounterpartFromOrder(order, isProviderView);
			if (!counterpart) return;
			const key = `${orderId}`;
			if (!groups.has(key)) {
				groups.set(key, {
					list: [],
					counterpartId: counterpart.id,
					recipientUserId: counterpart.recipientUserId,
					orderId,
					name: counterpart.name,
					avatarUrl: resolveProfileImageUrl(
						isProviderView
							? order.clients?.users?.image_profile
							: order.providers?.users?.image_profile,
					),
				});
				return;
			}

			const existing = groups.get(key);
			if (!existing) return;
			if (
				(!existing.recipientUserId || existing.recipientUserId <= 0) &&
				counterpart.recipientUserId
			) {
				existing.recipientUserId = counterpart.recipientUserId;
			}
			if (
				(!existing.counterpartId || existing.counterpartId <= 0) &&
				counterpart.id
			) {
				existing.counterpartId = counterpart.id;
			}
			if (!existing.name && counterpart.name) {
				existing.name = counterpart.name;
			}
			if (!existing.avatarUrl) {
				existing.avatarUrl = resolveProfileImageUrl(
					isProviderView
						? order.clients?.users?.image_profile
						: order.providers?.users?.image_profile,
				);
			}
		});

		const result = Array.from(groups.values()).map((group) => {
			const list = [...group.list].sort((a, b) => {
				const ad = a.sent_at ? new Date(a.sent_at).getTime() : 0;
				const bd = b.sent_at ? new Date(b.sent_at).getTime() : 0;
				if (ad === bd) return Number(a.id ?? 0) - Number(b.id ?? 0);
				return ad - bd;
			});
			const last = list.length > 0 ? list[list.length - 1] : null;
			const lastTimestamp = last?.sent_at
				? new Date(last.sent_at).getTime()
				: Number(group.orderId);
			const counterpartInfo =
				last && last.reminent_id === group.counterpartId
					? last.users_Reminent
					: last?.users_Recipient;
			const name =
				toFullName(counterpartInfo) ||
				group.name ||
				(group.counterpartId > 0 ? `Usuario #${group.counterpartId}` : "Usuario");
			const unread = list.filter(
				(m) => m.recipient_id === numericUserId && !m.is_read,
			).length;
			const previewHasImage = Boolean(last?.archive_url);
			const orderCode =
				orderCodeByOrderId.get(group.orderId) ?? String(group.orderId);
			const previewText = (last ? getMessagePreviewText(last) : "").trim();

			return {
				id: `${group.orderId}`,
				orderId: group.orderId,
				orderCode,
				counterpartId: group.counterpartId,
				recipientUserId: group.recipientUserId,
				name,
				preview: previewText || (previewHasImage ? "Imagen" : ""),
				previewHasImage,
				avatarUrl:
					group.avatarUrl ||
					resolveProfileImageUrl(counterpartInfo?.image_profile),
				unread,
				timeLabel: formatConversationSidebarTimeLabel(last?.sent_at),
				lastTimestamp,
			};
		});

		return result
			.sort((a, b) => b.lastTimestamp - a.lastTimestamp)
			.map(({ lastTimestamp: _lastTimestamp, ...conversation }) => conversation);
	}, [allUserMessages, numericUserId, allOrders, isProviderView, orderCodeByOrderId]);

	const conversationRows: Conversation[] = useMemo(
		() =>
			conversationsVm.map((c) => ({
				id: c.id,
				name: c.name,
				preview: c.preview,
				hasImagePreview: c.previewHasImage,
				timeLabel: c.timeLabel,
				unread: c.unread,
				online:
					Boolean(c.recipientUserId) &&
					onlineUserIds.has(Number(c.recipientUserId)),
			})),
		[conversationsVm, onlineUserIds],
	);

	const avatarByConversationId = useMemo(() => {
		const next = new Map<string, string>();
		conversationsVm.forEach((conversation) => {
			const resolved = resolveProfileImageUrl(conversation.avatarUrl);
			if (resolved) next.set(conversation.id, resolved);
		});
		return next;
	}, [conversationsVm]);

	const [rendered, setRendered] = useState(false);
	const [active, setActive] = useState(false);
	const [draft, setDraft] = useState("");
	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");
	const [viewerImageUrl, setViewerImageUrl] = useState<string>("");
	const [floatingDateLabel, setFloatingDateLabel] = useState("");
	const [showFloatingDateLabel, setShowFloatingDateLabel] = useState(false);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const [showCounterpartProfileModal, setShowCounterpartProfileModal] = useState(false);
	const conversationListRef = useRef<HTMLDivElement | null>(null);
	const threadListRef = useRef<HTMLDivElement | null>(null);
	const imageInputRef = useRef<HTMLInputElement | null>(null);
	const floatingDateTimeoutRef = useRef<number | undefined>(undefined);
	const infoModalStorageKey = useMemo(
		() => `${CHAT_INFO_MODAL_STORAGE_PREFIX}:${numericUserId || "guest"}`,
		[numericUserId],
	);

	const activeConversation = useMemo<ConversationVm | null>(() => {
		if (!conversationsVm.length) return null;
		const match = conversationsVm.find((c) => c.id === activeConversationId);
		return match ?? conversationsVm[0];
	}, [activeConversationId, conversationsVm]);

	const safeOrderId = activeConversation?.orderId ?? conversationsVm[0]?.orderId ?? 0;
	const { upload: uploadMessageFile, loading: uploadingFile } =
		useUploadMessageFile(safeOrderId);
	const { data: activeOrderRaw, execute: refetchActiveOrder } = useOrder(safeOrderId);
	const activeOrder = useMemo(() => normalizeOrder(activeOrderRaw), [activeOrderRaw]);
	const activeOrderStatus =
		socketOrderStatusByOrder[safeOrderId] ?? activeOrder?.status_order ?? "";
	const activeOrderStatusLabel = normalizeOrderStatusLabel(activeOrderStatus);
	const isClosedOrder = CLOSED_ORDER_STATUSES.has(activeOrderStatus);
	const isReportBlockedOrder = REPORT_BLOCKED_ORDER_STATUSES.has(activeOrderStatus);
	const canReportIssue =
		!isReportBlockedOrder && Boolean(SUPPORT_WHATSAPP_REPORT_NUMBER);
	const orderDerivedRecipientUserId = useMemo(
		() => getCounterpartUserIdFromOrder(activeOrderRaw, isProviderView),
		[activeOrderRaw, isProviderView],
	);
	const resolvedRecipientFromOrderMap = resolvedRecipientByOrder[safeOrderId] ?? null;
	const effectiveRecipientUserId =
		activeConversation?.recipientUserId ??
		resolvedRecipientFromOrderMap ??
		orderDerivedRecipientUserId ??
		null;
	const activeHeaderAvatar = useMemo(() => {
		const fromConversation = resolveProfileImageUrl(activeConversation?.avatarUrl);
		if (fromConversation) return fromConversation;
		const orderPayload = getPayloadRecord(activeOrderRaw);
		const counterpartFromOrder = isProviderView
			? (orderPayload?.clients as Record<string, unknown> | undefined)
			: (orderPayload?.providers as Record<string, unknown> | undefined);
		const counterpartUsers = toRecord(counterpartFromOrder?.users);
		return resolveProfileImageUrl(toText(counterpartUsers?.image_profile));
	}, [activeConversation?.avatarUrl, activeOrderRaw, isProviderView]);
	const activeHeaderInitials = useMemo(
		() => getInitials(activeConversation?.name ?? "Chat"),
		[activeConversation?.name],
	);
	const counterpartProfileData = useMemo(() => {
		const orderPayload = getPayloadRecord(activeOrderRaw);
		const orderAddress = toRecord(orderPayload?.addresses);
		const counterpartFromOrder = isProviderView
			? (orderPayload?.clients as Record<string, unknown> | undefined)
			: (orderPayload?.providers as Record<string, unknown> | undefined);
		const counterpart = toRecord(counterpartFromOrder?.users);

		const fullName =
			[toText(counterpart?.names), toText(counterpart?.last_names)]
				.filter(Boolean)
				.join(" ") ||
			toText(activeConversation?.name) ||
			"Usuario";

		const gender = resolveGenderLabel(
			counterpartFromOrder?.gender_client ??
				counterpart?.gender_client ??
				counterpart?.gender,
		);

		const directAge =
			toPositiveNumber(counterpartFromOrder?.age) ??
			toPositiveNumber(counterpart?.age);
		const age =
			directAge !== null
				? String(directAge)
				: calculateAgeFromDate(
						counterpartFromOrder?.date_of_birth ?? counterpart?.date_of_birth,
					);

		const primaryAddress =
			toText(orderAddress?.name_address) ??
			toText(orderAddress?.street_address) ??
			"Dirección no disponible";

		const secondaryAddress = toText(orderAddress?.reference_address) ?? "";

		return {
			fullName,
			gender,
			age,
			primaryAddress,
			secondaryAddress,
		};
	}, [activeConversation?.name, activeOrderRaw, isProviderView]);

	const messages = useMemo(() => {
		if (!activeConversation) return [];
		return allUserMessages
			.filter(
				(m) =>
					m.order_id === activeConversation.orderId &&
					(activeConversation.counterpartId <= 0 ||
						m.reminent_id === activeConversation.counterpartId ||
						m.recipient_id === activeConversation.counterpartId),
			)
			.sort((a, b) => {
				const ad = a.sent_at ? new Date(a.sent_at).getTime() : 0;
				const bd = b.sent_at ? new Date(b.sent_at).getTime() : 0;
				if (ad === bd) return Number(a.id ?? 0) - Number(b.id ?? 0);
				return ad - bd;
			});
	}, [allUserMessages, activeConversation]);

	const visibleMessages = useMemo(
		() =>
			messages.map((m) => {
				const basePayload = {
					id:
						String(m.id ?? "") ||
						`${m.order_id}-${m.reminent_id}-${m.recipient_id}-${m.content_message}`,
					from:
						m.reminent_id === numericUserId
							? ("me" as const)
							: ("other" as const),
					isRead: Boolean(m.is_read),
					dateKey: getMessageDateKey(m.sent_at),
					dateLabel: formatWhatsappDateLabel(m.sent_at),
					time: formatShortTime(m.sent_at),
				};

				const quotePayload = parseQuotationMessagePayload(
					m.content_message,
					m.quotations,
				);
				const paymentNoticePayload = parsePaymentRegisteredMessagePayload(
					m.content_message,
				);
				const statusChangedPayload = parseOrderStatusChangedMessagePayload(
					m.content_message,
				);
				if (quotePayload) {
					return {
						...basePayload,
						kind: "quote" as const,
						quoteCode: `COT-${String(quotePayload.id).padStart(3, "0")}`,
						priceLabel: formatCurrency(quotePayload.proposed_price),
						description: quotePayload.description_quotation || undefined,
					} as ChatMessage;
				}

				if (paymentNoticePayload) {
					return {
						...basePayload,
						kind: "payment_notice" as const,
						text: paymentNoticePayload.message ?? "",
					} as ChatMessage;
				}

				if (statusChangedPayload) {
					const statusCopy = getOrderStatusChangeCopy({
						newStatus: statusChangedPayload.new_status,
						previousStatus: statusChangedPayload.previous_status,
						fallbackMessage: statusChangedPayload.message,
					});
					return {
						...basePayload,
						kind: "order_status_notice" as const,
						text: statusCopy.message,
						status: normalizeOrderStatusLabel(
							statusChangedPayload.new_status,
						),
					} as ChatMessage;
				}

				if (m.archive_url) {
					return {
						...basePayload,
						kind: "image" as const,
						imageUrl: resolveAttachmentUrl(m.archive_url),
						text: m.content_message || undefined,
					} as ChatMessage;
				}

				return {
					...basePayload,
					kind: "text" as const,
					text: m.content_message,
				} as ChatMessage;
			}),
		[messages, numericUserId],
	);

	const canSend = useMemo(
		() =>
			(draft.trim().length > 0 || Boolean(selectedImage)) &&
			Number(effectiveRecipientUserId ?? 0) > 0 &&
			socketReady &&
			!isClosedOrder &&
			!sendingMessage &&
			!uploadingFile,
		[
			draft,
			selectedImage,
			effectiveRecipientUserId,
			socketReady,
			isClosedOrder,
			sendingMessage,
			uploadingFile,
		],
	);

	const updateFloatingDateLabel = useCallback(() => {
		const container = threadListRef.current;
		if (!container) return;
		const dateNodes = Array.from(
			container.querySelectorAll<HTMLElement>("[data-message-date]"),
		);
		if (!dateNodes.length) return;

		const scrollTop = container.scrollTop;
		let currentLabel = dateNodes[0].dataset.messageDate ?? "";

		for (const node of dateNodes) {
			if (node.offsetTop - 8 <= scrollTop) {
				currentLabel = node.dataset.messageDate ?? currentLabel;
			} else {
				break;
			}
		}

		if (!currentLabel) return;
		setFloatingDateLabel(currentLabel);
		setShowFloatingDateLabel(true);

		if (floatingDateTimeoutRef.current) {
			window.clearTimeout(floatingDateTimeoutRef.current);
		}
		floatingDateTimeoutRef.current = window.setTimeout(() => {
			setShowFloatingDateLabel(false);
		}, 900);
	}, []);

	useEffect(() => {
		return () => {
			if (imagePreviewUrl) {
				URL.revokeObjectURL(imagePreviewUrl);
			}
		};
	}, [imagePreviewUrl]);

	const clearSelectedImage = useCallback(() => {
		if (imagePreviewUrl) {
			URL.revokeObjectURL(imagePreviewUrl);
		}
		setSelectedImage(null);
		setImagePreviewUrl("");
		if (imageInputRef.current) imageInputRef.current.value = "";
	}, [imagePreviewUrl]);

	const onPickImage = useCallback(() => {
		if (isClosedOrder) return;
		imageInputRef.current?.click();
	}, [isClosedOrder]);

	const onImageSelected = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (isClosedOrder) return;
			const file = event.target.files?.[0];
			if (!file) return;
			if (!file.type.startsWith("image/")) return;
			if (imagePreviewUrl) {
				URL.revokeObjectURL(imagePreviewUrl);
			}
			setSelectedImage(file);
			setImagePreviewUrl(URL.createObjectURL(file));
		},
		[imagePreviewUrl, isClosedOrder],
	);

	const filteredConversations = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return conversationRows;
		return conversationRows.filter((c) => {
			return (
				c.name.toLowerCase().includes(q) ||
				c.preview.toLowerCase().includes(q) ||
				c.timeLabel.toLowerCase().includes(q)
			);
		});
	}, [conversationRows, query]);

	const isConversationListLoading =
		(loadingMessages || isLoadingOrders || isSocketInitializing) &&
		filteredConversations.length === 0;
	const isThreadLoading =
		(loadingMessages || isLoadingOrders || isSocketInitializing) &&
		safeOrderId > 0 &&
		visibleMessages.length === 0;

	const totalUnreadCount = useMemo(
		() =>
			conversationRows.reduce(
				(total, conversation) => total + conversation.unread,
				0,
			),
		[conversationRows],
	);

	const close = useCallback(() => {
		setActive(false);
		setTimeout(() => {
			setRendered(false);
			onClose();
		}, 200);
	}, [onClose]);

	const dismissInfoModal = useCallback(() => {
		setShowInfoModal(false);
		try {
			window.localStorage.setItem(infoModalStorageKey, "1");
		} catch {
			// Non-critical: ignore storage failures.
		}
	}, [infoModalStorageKey]);

	const expandToPage = useCallback(() => {
		// Close immediately so it doesn't pop back open
		// when the user returns from /chat-expanded.
		onClose();
		navigate("/chat-expanded", {
			state: {
				providerName: activeConversation?.name,
				orderId: activeConversation?.orderId,
			},
		});
	}, [activeConversation?.name, activeConversation?.orderId, navigate, onClose]);

	useEffect(() => {
		let timeoutId: number | undefined;
		let rafId: number | undefined;

		if (open) {
			timeoutId = window.setTimeout(() => {
				setScreen("list");
				setQuery("");
				setRendered(true);
			}, 0);
			rafId = window.requestAnimationFrame(() => setActive(true));
			return () => {
				if (timeoutId) window.clearTimeout(timeoutId);
				if (rafId) window.cancelAnimationFrame(rafId);
			};
		}

		rafId = window.requestAnimationFrame(() => setActive(false));
		timeoutId = window.setTimeout(() => setRendered(false), 200);
		return () => {
			if (timeoutId) window.clearTimeout(timeoutId);
			if (rafId) window.cancelAnimationFrame(rafId);
		};
	}, [open]);

	useEffect(() => {
		if (!open) {
			setShowInfoModal(false);
			setShowCounterpartProfileModal(false);
			return;
		}

		try {
			const hasSeen = window.localStorage.getItem(infoModalStorageKey) === "1";
			setShowInfoModal(!hasSeen);
		} catch {
			setShowInfoModal(true);
		}
	}, [open, infoModalStorageKey]);

	useEffect(() => {
		if (!rendered) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			if (showInfoModal) {
				dismissInfoModal();
				return;
			}
			if (showCounterpartProfileModal) {
				setShowCounterpartProfileModal(false);
				return;
			}
			if (viewerImageUrl) {
				setViewerImageUrl("");
				return;
			}
			close();
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [
		rendered,
		close,
		viewerImageUrl,
		showInfoModal,
		dismissInfoModal,
		showCounterpartProfileModal,
	]);

	useEffect(() => {
		if (!rendered) return;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [rendered]);

	useEffect(() => {
		if (!activeConversationId && conversationsVm.length > 0) {
			setActiveConversationId(conversationsVm[0].id);
		}
	}, [activeConversationId, conversationsVm]);

	useEffect(() => {
		const refreshEventName = "servix:chat-refresh";
		const handleChatRefresh = (event: Event) => {
			const customEvent = event as CustomEvent<{
				senderUserId?: number;
				recipientUserId?: number;
			}>;
			const senderUserId = Number(customEvent.detail?.senderUserId ?? 0);
			const recipientUserId = Number(customEvent.detail?.recipientUserId ?? 0);

			if (
				numericUserId > 0 &&
				senderUserId > 0 &&
				recipientUserId > 0 &&
				senderUserId !== numericUserId &&
				recipientUserId !== numericUserId
			) {
				return;
			}

			scheduleMessagesRefresh();
			if (isProviderView) {
				void refetchProviderOrders();
			} else {
				void refetchClientOrders();
			}
		};

		window.addEventListener(refreshEventName, handleChatRefresh);
		return () => {
			window.removeEventListener(refreshEventName, handleChatRefresh);
		};
	}, [
		numericUserId,
		isProviderView,
		scheduleMessagesRefresh,
		refetchProviderOrders,
		refetchClientOrders,
	]);

	useEffect(() => {
		return () => {
			if (messageRefreshTimerRef.current !== null) {
				window.clearTimeout(messageRefreshTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!numericUserId) {
			setSocketReady(false);
			setIsSocketInitializing(false);
			return;
		}
		let cancelled = false;
		setIsSocketInitializing(true);
		const timeoutId = window.setTimeout(() => {
			if (cancelled) return;
			setSocketReady(false);
			setIsSocketInitializing(false);
		}, 5000);
		registerRef
			.current(numericUserId)
			.then((payload) => {
				if (cancelled) return;
				const incoming = Array.isArray(payload?.onlineUserIds)
					? payload.onlineUserIds
					: [];
				setOnlineUserIds(new Set(incoming));
				setSocketReady(true);
			})
			.catch(() => {
				if (!cancelled) setSocketReady(false);
			})
			.finally(() => {
				if (cancelled) return;
				window.clearTimeout(timeoutId);
				setIsSocketInitializing(false);
			});
		return () => {
			cancelled = true;
			window.clearTimeout(timeoutId);
		};
	}, [numericUserId]);

	useEffect(() => {
		if (!socketReady || !activeConversation?.orderId) return;
		const orderId = activeConversation.orderId;
		void joinRef.current(orderId);
		return () => {
			void leaveRef.current(orderId);
		};
	}, [socketReady, activeConversation?.orderId]);

	useOnOrderMessage((payload) => {
		const nextStatus = parseSocketOrderStatus(payload);
		if (nextStatus) {
			setSocketOrderStatusByOrder((prev) => {
				if (prev[nextStatus.orderId] === nextStatus.status) return prev;
				return { ...prev, [nextStatus.orderId]: nextStatus.status };
			});
		}
		scheduleMessagesRefresh();
	});

	useOnMessageReceived(() => {
		onIncomingMessage?.();
		scheduleMessagesRefresh();
	});

	useOnMessageRead(() => {
		scheduleMessagesRefresh();
	});

	useOnChatFinalized(() => {
		if (safeOrderId > 0) {
			setSocketOrderStatusByOrder((prev) => {
				if (prev[safeOrderId] === "Completado") return prev;
				return { ...prev, [safeOrderId]: "Completado" };
			});
		}
		scheduleMessagesRefresh();
		void refetchActiveOrder();
	});

	useOnUserOnline((payload) => {
		const nextId = Number(payload?.userId ?? 0);
		if (!nextId) return;
		setOnlineUserIds((prev) => {
			if (prev.has(nextId)) return prev;
			const next = new Set(prev);
			next.add(nextId);
			return next;
		});
	});

	useOnUserOffline((payload) => {
		const nextId = Number(payload?.userId ?? 0);
		if (!nextId) return;
		setOnlineUserIds((prev) => {
			if (!prev.has(nextId)) return prev;
			const next = new Set(prev);
			next.delete(nextId);
			return next;
		});
	});

	useEffect(() => {
		onUnreadCountChange?.(totalUnreadCount);
	}, [onUnreadCountChange, totalUnreadCount]);

	useEffect(() => {
		if (!activeConversation?.orderId) return;
		if (Number(activeConversation.recipientUserId ?? 0) > 0) return;
		if (Number(resolvedRecipientByOrder[activeConversation.orderId] ?? 0) > 0) return;

		const orderInfo = allOrders.find(
			(o) => Number(o.id) === Number(activeConversation.orderId),
		);
		if (!orderInfo) return;

		const counterpartUserId = isProviderView
			? Number(orderInfo.clients?.user_id ?? orderInfo.clients?.users?.id ?? 0)
			: Number(orderInfo.providers?.users?.id ?? 0);

		if (!Number.isFinite(counterpartUserId) || counterpartUserId <= 0) return;

		setResolvedRecipientByOrder((prev) => {
			if (prev[activeConversation.orderId] === counterpartUserId) return prev;
			return { ...prev, [activeConversation.orderId]: counterpartUserId };
		});
	}, [
		activeConversation?.orderId,
		activeConversation?.recipientUserId,
		allOrders,
		isProviderView,
		resolvedRecipientByOrder,
	]);

	useEffect(() => {
		if (screen !== "thread") return;
		if (!activeConversation || !numericUserId) return;
		const unread = messages.filter(
			(m) =>
				m.recipient_id === numericUserId &&
				!m.is_read &&
				typeof m.id === "number" &&
				!markedMessageIdsRef.current.has(m.id),
		);
		if (!unread.length) return;

		unread.forEach((m) => {
			if (typeof m.id === "number") markedMessageIdsRef.current.add(m.id);
		});

		void Promise.all(
			unread.map((m) =>
				markAsReadRef.current(Number(m.id), numericUserId).catch(() => {
					markedMessageIdsRef.current.delete(Number(m.id));
				}),
			),
		).then(() => {
			scheduleMessagesRefresh();
		});
	}, [screen, messages, activeConversation, numericUserId, scheduleMessagesRefresh]);

	useEffect(() => {
		if (!rendered || screen !== "thread") return;
		window.requestAnimationFrame(() => {
			threadListRef.current?.scrollTo({
				top: threadListRef.current?.scrollHeight ?? 0,
				behavior: "auto",
			});
			updateFloatingDateLabel();
		});
	}, [
		rendered,
		screen,
		activeConversation?.id,
		visibleMessages.length,
		updateFloatingDateLabel,
	]);

	useEffect(() => {
		return () => {
			if (floatingDateTimeoutRef.current) {
				window.clearTimeout(floatingDateTimeoutRef.current);
			}
		};
	}, []);

	const closeImageViewer = () => {
		setViewerImageUrl("");
	};

	if (!rendered) return null;

	const openThread = (id: string) => {
		setActiveConversationId(id);
		setScreen("thread");
		setShowCounterpartProfileModal(false);
	};

	const goToOrderDetails = () => {
		if (!activeConversation?.orderId) return;
		navigate(`/info/pedido/${activeConversation.orderId}`);
	};

	const openCounterpartProfile = () => {
		if (!activeConversation) return;
		setShowCounterpartProfileModal(true);
	};

	const reportCounterpartIssue = () => {
		if (!canReportIssue) return;
		const counterpartRole = isProviderView ? "cliente" : "proveedor";
		const counterpartName =
			counterpartProfileData.fullName?.trim() ||
			(isProviderView ? "Cliente" : "Proveedor");
		const url = buildSupportWhatsAppReportUrl(
			`Hola, me gustaría reportar a este ${counterpartRole}: ${counterpartName}.`,
		);
		if (!url) return;
		setShowCounterpartProfileModal(false);
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const send = async () => {
		if (!canSend || !activeConversation || !numericUserId) return;
		const text = draft.trim();
		setDraft("");
		try {
			const recipientId = Number(effectiveRecipientUserId ?? 0);
			if (!recipientId) {
				setDraft(text);
				return;
			}
			let archiveUrl: string | undefined;
			if (selectedImage) {
				const uploadResponse = await uploadMessageFile(selectedImage);
				archiveUrl =
					typeof uploadResponse?.url === "string"
						? uploadResponse.url
						: undefined;
			}
			await sendMessage({
				reminent_id: numericUserId,
				recipient_id: recipientId,
				order_id: activeConversation.orderId,
				content_message: text,
				archive_url: archiveUrl,
			});
			clearSelectedImage();
			scheduleMessagesRefresh();
			window.requestAnimationFrame(() => {
				threadListRef.current?.scrollTo({
					top: threadListRef.current?.scrollHeight ?? 0,
					behavior: "smooth",
				});
			});
		} catch {
			setDraft(text);
		}
	};

	return (
		<div className='fixed inset-0 z-40'>
			<button
				type='button'
				aria-label='Cerrar'
				className={
					"cursor-pointer absolute inset-0 bg-black/20 transition-opacity duration-250 ease-out " +
					(active ? "opacity-100" : "opacity-0")
				}
				onClick={close}
			/>

			<div
				className='relative flex min-h-full items-end justify-center p-0 sm:items-end sm:justify-end sm:p-6'
				onClick={close}
			>
				<div
					role='dialog'
					aria-modal='true'
					onClick={(e) => e.stopPropagation()}
					className={
						"relative flex h-dvh w-full max-w-none flex-col overflow-hidden rounded-none bg-white shadow-xl transition-all duration-250 ease-out transform-gpu " +
						"min-h-0 mb-0 sm:mb-24 sm:h-[min(42rem,calc(100dvh-8rem))] sm:max-w-md sm:rounded-3xl " +
						" " +
						(active
							? "translate-y-0 opacity-100"
							: "translate-y-6 opacity-0") +
						" " +
						(className ?? "")
					}
				>
					{screen === "list" ? (
						<>
							<div className='flex items-center justify-between bg-primary px-4 py-3.5 text-white sm:px-5 sm:py-4'>
								<div className='flex items-center gap-2.5 sm:gap-3'>
									<MessageCircle className='h-6 w-6 fill-white' />
									<div className='text-[15px] font-semibold sm:text-base'>
										{isProviderView ? "Mensajes" : "Mensajes"}
									</div>
								</div>
								<div className='flex items-center gap-1.5 sm:gap-2'>
									<button
										type='button'
										aria-label='Expandir'
										className='cursor-pointer rounded-full p-2 hover:bg-white/10 transition-all'
										onClick={expandToPage}
									>
										<Expand className='h-5 w-5' />
									</button>
									<button
										type='button'
										aria-label='Cerrar'
										onClick={close}
										className='cursor-pointer rounded-full p-2 hover:bg-white/10 transition-all'
									>
										<X className='h-5 w-5' />
									</button>
								</div>
							</div>

							<div className='border-b border-(--border) bg-white px-3 py-3 sm:px-5 sm:py-4'>
								<div className='flex items-center gap-2 rounded-xl border border-(--border) bg-white px-3 py-2.5 sm:gap-3 sm:px-4 sm:py-3'>
									<Search className='h-4.5 w-4.5 text-neutral-400 sm:h-5 sm:w-5' />
									<input
										value={query}
										onChange={(e) => setQuery(e.target.value)}
										placeholder='Buscar conversaciones...'
										className='w-full bg-transparent text-sm text-neutral-700 outline-none placeholder:text-neutral-400 sm:text-base'
									/>
								</div>
							</div>

							<div className='relative min-h-0 flex-1 bg-[#fafafa] px-3 py-3 text-base sm:px-5 sm:py-4'>
								<div
									ref={conversationListRef}
									className='h-full overflow-y-auto overscroll-contain scroll-smooth'
								>
									{isConversationListLoading ? (
										<div className='flex items-center justify-center px-3 py-6'>
											<Loader className='h-16 w-16' />
										</div>
									) : null}
									{filteredConversations.map((c, idx) => (
										<button
											key={`${c.id}-${idx}`}
											type='button'
											onClick={() => openThread(c.id)}
											className={
												"cursor-pointer flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-neutral-100 sm:gap-4 sm:px-5 sm:py-4 " +
												(idx === 0
													? ""
													: "border-t border-(--border)")
											}
										>
											<div className='relative h-11 w-11 sm:h-12 sm:w-12'>
												<div className='h-11 w-11 overflow-hidden rounded-full bg-neutral-200 sm:h-12 sm:w-12'>
													{avatarByConversationId.get(c.id) ? (
														<img
															src={avatarByConversationId.get(
																c.id,
															)}
															alt={c.name}
															className='h-full w-full object-cover'
														/>
													) : (
														<div className='flex h-full w-full items-center justify-center text-slate-700 text-sm font-semibold uppercase'>
															{getInitials(c.name) || "--"}
														</div>
													)}
												</div>
												{c.online ? (
													<span className='absolute -bottom-1 -right-1 z-20 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500 shadow-sm' />
												) : null}
											</div>

											<div className='min-w-0 flex-1'>
												<div className='text-[15px] font-semibold text-neutral-900 sm:text-base'>
													{c.name}
												</div>
												<div className='mt-1 flex items-center gap-1 truncate text-xs text-neutral-400 sm:text-sm'>
													{c.hasImagePreview ? (
														<ImageIcon className='h-3.5 w-3.5 shrink-0' />
													) : null}
													<span className='truncate'>
														{c.preview}
													</span>
												</div>
											</div>

											<div className='flex shrink-0 flex-col items-end'>
												<div className='text-xs text-neutral-400 sm:text-sm'>
													{c.timeLabel}
												</div>
												{c.unread > 0 ? (
													<div className='mt-1.5 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-semibold text-white sm:mt-2 sm:text-sm'>
														{c.unread > 99 ? "+99" : c.unread}
													</div>
												) : null}
											</div>
										</button>
									))}
									{!isConversationListLoading &&
									filteredConversations.length === 0 ? (
										<div className='px-3 py-5 text-center text-xs text-neutral-500 sm:px-5 sm:text-sm'>
											No hay conversaciones disponibles.
										</div>
									) : null}
								</div>
							</div>
						</>
					) : (
						<>
							<div className='flex items-center justify-between bg-primary px-4 py-3.5 text-white sm:px-5 sm:py-4'>
								<div className='flex items-center gap-2.5 sm:gap-3'>
									<MessageCircle className='h-6 w-6 text-white fill-white' />
									<div className='min-w-0'>
										<div className='text-[15px] font-semibold sm:text-base'>
											{"Mensajes"}
										</div>
									</div>
								</div>

								<div className='flex items-center gap-1.5 sm:gap-2'>
									<button
										type='button'
										aria-label='Expandir'
										className='rounded-full p-2 hover:bg-white/10 cursor-pointer transition-all'
										onClick={expandToPage}
									>
										<Expand className='h-5 w-5' />
									</button>
									<button
										type='button'
										aria-label='Cerrar'
										onClick={close}
										className='rounded-full p-2 hover:bg-white/10 cursor-pointer transition-all'
									>
										<X className='h-5 w-5' />
									</button>
								</div>
							</div>

							<div className='flex items-center gap-3 border-b border-(--border) bg-white px-3 py-2.5 sm:px-5 sm:py-3'>
								<button
									type='button'
									onClick={() => setScreen("list")}
									className='cursor-pointer text-neutral-500 hover:text-neutral-700'
									aria-label='Volver'
								>
									<ChevronLeft className='h-6 w-6 mr-2' />
								</button>
								<button
									type='button'
									onClick={openCounterpartProfile}
									className='relative h-9 w-9 cursor-pointer rounded-full sm:h-10 sm:w-10'
									aria-label='Ver perfil'
								>
									<div className='h-9 w-9 overflow-hidden rounded-full bg-neutral-200 sm:h-10 sm:w-10'>
										{activeHeaderAvatar ? (
											<img
												src={activeHeaderAvatar}
												alt={activeConversation?.name ?? "Chat"}
												className='h-full w-full object-cover'
											/>
										) : (
											<div className='flex h-full w-full items-center justify-center text-slate-700 text-xs font-semibold uppercase'>
												{activeHeaderInitials || "--"}
											</div>
										)}
									</div>
									{Boolean(effectiveRecipientUserId) &&
									onlineUserIds.has(
										Number(effectiveRecipientUserId),
									) ? (
										<span className='absolute -bottom-1 -right-1 z-20 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 shadow-sm' />
									) : null}
								</button>
								<div className='min-w-0'>
									<button
										type='button'
										onClick={openCounterpartProfile}
										className='cursor-pointer truncate text-left text-sm font-semibold text-neutral-900 hover:underline sm:text-base'
									>
										{activeConversation?.name ?? "Chat"}
									</button>
									<div className='mt-0.5 flex items-center gap-2 text-xs text-neutral-500 sm:text-sm'>
										<button
											type='button'
											onClick={goToOrderDetails}
											className='cursor-pointer text-primary hover:underline'
										>
											{activeConversation
												? `Pedido #${activeConversation.orderCode}`
												: "Pedido"}
										</button>
									</div>
								</div>
							</div>

							<div className='relative flex min-h-0 flex-1 flex-col'>
								<div className='relative min-h-0 flex-1 px-3 py-3 text-base sm:px-5 sm:py-4'>
									{showFloatingDateLabel && floatingDateLabel ? (
										<div className='pointer-events-none absolute left-1/2 top-2 z-30 -translate-x-1/2'>
											<span className='rounded-full border border-(--border) bg-white px-2.5 py-1 text-[11px] text-neutral-500 shadow-sm sm:px-3 sm:text-xs'>
												{floatingDateLabel}
											</span>
										</div>
									) : null}
									<div
										ref={threadListRef}
										onScroll={updateFloatingDateLabel}
										className='chat-pattern-bg h-full overflow-y-auto overscroll-contain bg-(--bg) px-1 scroll-smooth'
									>
										<div className='mb-5 flex justify-center sm:mb-6'>
											<div className='flex w-full items-start gap-3 rounded-2xl bg-blue-50 px-3 py-3 text-left shadow-sm shadow-[#afc0f1] sm:rounded-lg sm:px-4'>
												<Info className='mt-0.5 h-7 w-7 shrink-0 text-blue-600 sm:h-8 sm:w-8' />
												<div>
													<ul className='list-disc pl-4 text-xs text-blue-800 opacity-90 space-y-1'>
														<li>
															No divulgues información
															personal (claves, datos
															bancarios, etc.).
														</li>
														<li>
															Tu chat puede ser revisado por
															el equipo de soporte de ser
															requerido.
														</li>
													</ul>
												</div>
											</div>
										</div>
										<div className='space-y-4'>
											{isThreadLoading ? (
												<div className='flex items-center justify-center py-6'>
													<Loader className='h-16 w-16' />
												</div>
											) : visibleMessages.length === 0 ? (
												<div className='rounded-md border border-slate-200 bg-white px-4 py-2 text-[11px] text-neutral-500 sm:text-xs'>
													Aún no se ha enviado mensajes.
												</div>
											) : null}
											{visibleMessages.map((m, index) => {
												const previousMessage =
													visibleMessages[index - 1];
												const showDateSeparator =
													index === 0 ||
													previousMessage?.dateKey !==
														m.dateKey;
												const viewFrom = m.from;
												return (
													<div
														key={`${m.id}-${index}`}
														data-message-date={
															m.dateLabel ?? ""
														}
													>
														{showDateSeparator &&
														m.dateLabel ? (
															<div className='my-2 flex justify-center'>
																<span className='rounded-full border border-(--border) bg-white px-2.5 py-1 text-[11px] text-neutral-500 shadow-sm sm:px-3 sm:text-xs'>
																	{m.dateLabel}
																</span>
															</div>
														) : null}
														<div
															className={
																"flex items-start gap-3 " +
																(viewFrom === "me"
																	? "justify-end"
																	: "justify-start")
															}
														>
															{viewFrom === "other" &&
															m.kind !== "payment_notice" &&
															m.kind !==
																"order_status_notice" ? (
																<div className='mt-1 h-10 w-10 overflow-hidden rounded-full bg-neutral-200'>
																	{activeHeaderAvatar ? (
																		<img
																			src={
																				activeHeaderAvatar
																			}
																			alt={
																				activeConversation?.name ??
																				"Chat"
																			}
																			className='h-full w-full object-cover'
																		/>
																	) : (
																		<div className='flex h-full w-full items-center justify-center text-slate-700 text-xs font-semibold uppercase'>
																			{activeHeaderInitials ||
																				"--"}
																		</div>
																	)}
																</div>
															) : null}

															{m.kind === "quote" ? (
																<div className='w-full max-w-[75%]'>
																	<QuoteMessageCard
																		quoteCode={
																			m.quoteCode
																		}
																		priceLabel={
																			m.priceLabel
																		}
																		description={
																			m.description
																		}
																		onViewDetails={
																			expandToPage
																		}
																	/>
																	<div className='mt-1 flex items-center justify-end gap-1 text-sm text-neutral-400'>
																		{m.time}
																		{viewFrom ===
																		"me" ? (
																			m.isRead ? (
																				<CheckCheck className='h-3.5 w-3.5' />
																			) : (
																				<Check className='h-3.5 w-3.5' />
																			)
																		) : null}
																	</div>
																</div>
															) : m.kind === "image" ? (
																<div
																	className={
																		"max-w-[75%] rounded-2xl px-3 py-3 text-base leading-7 " +
																		(viewFrom === "me"
																			? "bg-primary text-white"
																			: "bg-white text-neutral-800 shadow-sm")
																	}
																>
																	<button
																		type='button'
																		onClick={() =>
																			setViewerImageUrl(
																				m.imageUrl,
																			)
																		}
																		className='w-full cursor-zoom-in'
																	>
																		<img
																			src={
																				m.imageUrl
																			}
																			alt='Imagen enviada'
																			className='max-h-64 w-full rounded-xl object-cover'
																		/>
																	</button>
																	{m.text ? (
																		<div className='mt-2 whitespace-pre-wrap wrap-anywhere'>
																			{m.text}
																		</div>
																	) : null}
																	<div
																		className={
																			"mt-1 flex items-center justify-end gap-1 text-sm " +
																			(viewFrom ===
																			"me"
																				? "text-white/80"
																				: "text-neutral-400")
																		}
																	>
																		{m.time}
																		{viewFrom ===
																		"me" ? (
																			m.isRead ? (
																				<CheckCheck className='h-3.5 w-3.5' />
																			) : (
																				<Check className='h-3.5 w-3.5' />
																			)
																		) : null}
																	</div>
																</div>
															) : m.kind ===
																	"payment_notice" ||
															  m.kind ===
																	"order_status_notice" ? (
																(() => {
																	const statusVisual =
																		m.kind ===
																		"order_status_notice"
																			? getOrderStatusVisual(
																					m.status,
																				)
																			: null;
																	const noticeIconKey: OrderStatusIconKey =
																		statusVisual?.iconKey ??
																		"check";
																	const NoticeIcon =
																		noticeIconKey ===
																		"message"
																			? MessageSquare
																			: noticeIconKey ===
																				  "clock"
																				? Clock3
																				: noticeIconKey ===
																					  "loader"
																					? LoaderCircle
																					: noticeIconKey ===
																						  "wrench"
																						? Wrench
																						: noticeIconKey ===
																							  "shield"
																							? ShieldCheck
																							: noticeIconKey ===
																								  "x"
																								? XCircle
																								: noticeIconKey ===
																									  "wallet"
																									? Wallet
																									: noticeIconKey ===
																										  "alert"
																										? AlertTriangle
																										: CheckCircle2;

																	const containerClassName =
																		m.kind ===
																		"order_status_notice"
																			? (statusVisual?.containerClassName ??
																				"border-amber-200 bg-amber-50")
																			: "border-[#F6D38C] bg-[#FFF7E6]";
																	const iconClassName =
																		m.kind ===
																		"order_status_notice"
																			? (statusVisual?.iconClassName ??
																				"text-amber-600")
																			: "text-[#C77800]";
																	const textClassName =
																		m.kind ===
																		"order_status_notice"
																			? (statusVisual?.textClassName ??
																				"text-amber-800")
																			: "text-[#A45C00]";

																	return (
																		<div className='my-2 flex w-full justify-center'>
																			<div
																				className={`flex max-w-[92%] items-center gap-2 rounded-xl border px-3 py-2 ${containerClassName}`}
																			>
																				<NoticeIcon
																					className={`h-4 w-4 ${iconClassName}`}
																				/>
																				<span
																					className={`text-center text-xs font-semibold ${textClassName}`}
																				>
																					{typeof m.text ===
																						"string" &&
																					m.text.trim()
																						.length >
																						0
																						? m.text
																						: m.kind ===
																							  "payment_notice"
																							? "El cliente ha pagado el servicio o los servicios solicitados."
																							: "El estado del pedido ha sido actualizado."}
																				</span>
																			</div>
																		</div>
																	);
																})()
															) : (
																<div
																	className={
																		"max-w-[75%] rounded-2xl px-4 py-3 text-base leading-7 " +
																		(viewFrom === "me"
																			? "bg-primary text-white"
																			: "bg-white text-neutral-800 shadow-sm")
																	}
																>
																	<div className='whitespace-pre-wrap wrap-anywhere'>
																		{m.text}
																	</div>
																	<div
																		className={
																			"mt-1 flex items-center justify-end gap-1 text-sm " +
																			(viewFrom ===
																			"me"
																				? "text-white/80"
																				: "text-neutral-400")
																		}
																	>
																		{m.time}
																		{viewFrom ===
																		"me" ? (
																			m.isRead ? (
																				<CheckCheck className='h-3.5 w-3.5' />
																			) : (
																				<Check className='h-3.5 w-3.5' />
																			)
																		) : null}
																	</div>
																</div>
															)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								</div>

								<div className='border-t border-(--border) bg-white px-3 py-2.5 sm:px-4 sm:py-3'>
									{isClosedOrder ? (
										<div className='mb-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700'>
											Este chat está bloqueado porque el pedido está
											en estado{" "}
											<span className='inline-flex max-w-44 items-center truncate whitespace-nowrap align-middle'>
												{activeOrderStatusLabel}
											</span>
											.
										</div>
									) : null}
									<input
										ref={imageInputRef}
										type='file'
										accept='image/*'
										aria-label='Seleccionar imagen'
										title='Seleccionar imagen'
										onChange={onImageSelected}
										className='hidden'
									/>
									{selectedImage && imagePreviewUrl ? (
										<div className='mb-3 rounded-xl border border-(--border) bg-white p-2'>
											<div className='relative'>
												<img
													src={imagePreviewUrl}
													alt='Previsualización'
													className='max-h-48 w-full rounded-lg object-cover'
												/>
												<button
													type='button'
													onClick={clearSelectedImage}
													aria-label='Eliminar imagen seleccionada'
													title='Eliminar imagen seleccionada'
													className='absolute right-2 top-2 rounded-full bg-black/60 p-1 text-white'
												>
													<X className='h-4 w-4' />
												</button>
											</div>
										</div>
									) : null}
									<div className='flex items-center gap-3'>
										<button
											type='button'
											aria-label='Adjuntar'
											onClick={onPickImage}
											disabled={
												isClosedOrder ||
												!socketReady ||
												uploadingFile
											}
											className='p-1 text-gray-400 hover:text-primary transition-colors cursor-pointer disabled:cursor-not-allowed disabled:text-gray-300'
										>
											<Image className='h-5 w-5' />
										</button>

										<input
											value={draft}
											onChange={(e) => setDraft(e.target.value)}
											aria-label='Escribe un mensaje'
											title='Escribe un mensaje'
											placeholder='Escribe un mensaje...'
											disabled={isClosedOrder || uploadingFile}
											className='w-full bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400 sm:text-base'
											onKeyDown={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													void send();
												}
											}}
										/>

										<button
											type='button'
											onClick={() => void send()}
											disabled={!canSend}
											className={
												"inline-flex h-9 w-12 items-center justify-center rounded-full text-white sm:h-10 sm:w-14 " +
												(canSend
													? "cursor-pointer bg-primary hover:bg-sky-600"
													: "cursor-not-allowed bg-sky-200")
											}
											aria-label='Enviar'
										>
											<Send className='h-5 w-5' />
										</button>
									</div>
								</div>

								<div
									className={
										"absolute inset-0 z-70 bg-[#efefef] transition-all duration-200 ease-out " +
										(showCounterpartProfileModal
											? "translate-x-0 opacity-100 pointer-events-auto"
											: "translate-x-6 opacity-0 pointer-events-none")
									}
								>
									<div className='relative flex h-full flex-col overflow-y-auto px-3 pb-4 pt-3 sm:px-4 sm:pb-5 sm:pt-4'>
										<div className='flex justify-end'>
											<button
												type='button'
												onClick={() =>
													setShowCounterpartProfileModal(false)
												}
												className='cursor-pointer rounded-full p-2 text-neutral-500 transition hover:bg-neutral-200 hover:text-neutral-700'
												aria-label='Cerrar perfil'
											>
												<X className='h-5 w-5' />
											</button>
										</div>

										<div className='mt-2 flex justify-center'>
											<div className='flex h-30 w-30 items-center justify-center overflow-hidden rounded-full bg-neutral-200'>
												{activeHeaderAvatar ? (
													<img
														src={activeHeaderAvatar}
														alt={
															counterpartProfileData.fullName
														}
														className='h-full w-full object-cover'
													/>
												) : (
													<span className='text-3xl font-bold uppercase text-neutral-600'>
														{activeHeaderInitials || "--"}
													</span>
												)}
											</div>
										</div>

										<h3 className='mt-4 text-center text-base font-medium text-neutral-700 sm:text-lg'>
											{counterpartProfileData.fullName}
										</h3>

										<div className='mt-5 grid grid-cols-2 gap-3'>
											<div>
												<p className='mb-1 text-sm font-medium text-neutral-600'>
													Género
												</p>
												<div className='rounded-xl border border-neutral-300 bg-[#f5f5f5] px-3 py-2 text-sm text-neutral-500'>
													{counterpartProfileData.gender}
												</div>
											</div>
											<div>
												<p className='mb-1 text-sm font-medium text-neutral-600'>
													Edad
												</p>
												<div className='rounded-xl border border-neutral-300 bg-[#f5f5f5] px-3 py-2 text-sm text-neutral-500'>
													{counterpartProfileData.age}
												</div>
											</div>
										</div>

										<div className='mt-5'>
											<p className='mb-1 text-sm font-medium text-neutral-600'>
												Dirección
											</p>
											<div className='flex items-center gap-2 rounded-xl border border-neutral-300 bg-[#f5f5f5] px-3 py-2'>
												<MapPin className='h-5 w-5 shrink-0 text-neutral-400' />
												<div className='min-w-0'>
													<p className='truncate text-sm text-neutral-500'>
														{
															counterpartProfileData.primaryAddress
														}
													</p>
													{counterpartProfileData.secondaryAddress ? (
														<p className='truncate text-xs text-neutral-400'>
															{
																counterpartProfileData.secondaryAddress
															}
														</p>
													) : null}
												</div>
											</div>
										</div>

										<button
											type='button'
											onClick={reportCounterpartIssue}
											disabled={!canReportIssue}
											className='mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-[#ea4646] bg-white px-4 py-2.5 text-sm font-semibold text-[#ea4646] transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60'
											aria-label='Reportar problema a soporte'
											title='Reportar problema a soporte'
										>
											<AlertTriangle className='h-4 w-4' />
											<span>
												{isReportBlockedOrder
													? "No disponible para este estado"
													: !SUPPORT_WHATSAPP_REPORT_NUMBER
														? "Configurar WhatsApp soporte"
														: "Reportar problema"}
											</span>
										</button>
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
			{showInfoModal ? (
				<div
					className='absolute inset-0 z-70 flex items-center justify-center bg-black/45 px-4'
					onClick={dismissInfoModal}
				>
					<div
						className='relative w-full max-w-xl rounded-3xl bg-white px-6 pb-6 pt-5 shadow-2xl sm:px-8'
						onClick={(event) => event.stopPropagation()}
					>
						<button
							type='button'
							aria-label='Cerrar aviso'
							onClick={dismissInfoModal}
							className='absolute right-4 top-4 cursor-pointer rounded-full p-2 text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-700'
						>
							<X className='h-6 w-6' />
						</button>

						<div className='flex justify-center pt-2'>
							<div className='flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary/20'>
								<Info className='h-8 w-8 text-primary' />
							</div>
						</div>

						<h3 className='mt-4 text-center text-2xl font-bold text-neutral-900'>
							¿Cómo funciona el chat?
						</h3>

						<p className='mt-5 text-center text-base leading-snug text-neutral-700'>
							Usa este espacio para coordinar los detalles del servicio con
							el cliente.
						</p>

						<p className='mt-4 text-center text-base leading-snug text-neutral-700'>
							Una vez que lleguen a un acuerdo,{" "}
							<span className='font-semibold text-primary'>
								envía una cotización
							</span>{" "}
							con el precio final para que el cliente pueda revisarla y
							confirmar el pago.
						</p>

						<button
							type='button'
							onClick={dismissInfoModal}
							className='mt-6 w-full cursor-pointer rounded-2xl bg-primary px-4 py-3 text-base font-semibold text-white transition-all duration-200 hover:bg-primary-hover active:scale-95'
						>
							Entendido
						</button>
					</div>
				</div>
			) : null}
			{viewerImageUrl ? (
				<div
					className='absolute inset-0 z-50 flex items-center justify-center bg-black/80 p-4'
					onClick={closeImageViewer}
				>
					<button
						type='button'
						aria-label='Cerrar imagen'
						onClick={closeImageViewer}
						className='absolute right-4 top-4 rounded-full bg-black/60 p-2 text-white'
					>
						<X className='h-5 w-5' />
					</button>
					<img
						src={viewerImageUrl}
						alt='Imagen ampliada'
						className='max-h-[90vh] max-w-[90vw] rounded-xl object-contain'
						onClick={(event) => event.stopPropagation()}
					/>
				</div>
			) : null}
		</div>
	);
}
export type { DialogChatProps } from "../../core/types/tsx/components/chat/DialogChat.types";
